import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import {
  UserIcon,
  PhoneIcon,
  CheckCircle2Icon,
  LoaderIcon,
  XCircleIcon,
  CirclePlus,
  BanIcon,
  UsersIcon,
  BabyIcon,
  Calendar,
  Euro,
  Ticket,
  Car,
  Mail,
  Clock,
  StretchVertical,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import OrderTimeline from "./OrderTimeline";

import { seperateDateAndTime } from "@/utils/formChange";
import axiosInstance from "@/utils/axiosInstance";
import AssignStatus from "@/components/common/orders/AssignStatus";
import { Driver, Orders } from "@/interface/entityInterface";
import AssignDriver from "@/components/common/orders/AssignDriver";
import Map from "@/components/map/Map";
import { useUserContext } from "@/context/UserContext";
import { convertMinToH } from "@/utils/generateTimeSlots";

interface Props {
  selectedOrder: Orders | null;
  isSheetOpen: boolean;
  setIsSheetOpen: (open: boolean) => void;
  setStatusChanged?: Dispatch<SetStateAction<boolean>>
}

const libraries: "places"[] = ["places"];

const OrderDetails: FC<Props> = ({
  selectedOrder,
  isSheetOpen,
  setIsSheetOpen,
  setStatusChanged
}) => {
  const { decodedToken } = useUserContext();
  const [drivers, setDrivers] = useState<Driver[]>([]);

  useEffect(() => {
    const getDrivers = async () => {
      const { data } = await axiosInstance.get("/drivers");
      setDrivers(data);
    };

    getDrivers();
  }, []);

  if (!selectedOrder) return;
  if (!isSheetOpen) return;

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetContent
        side="bottom"
        className="p-4 sm:p-5 max-h-[90vh] overflow-y-auto"
      >
        <SheetTitle className="hidden"></SheetTitle>
        {/* <h2 className="text-[30px]">{data?.dropoff}</h2> */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8 h-full">
          <Map
            libraries={libraries}
            coordinates={{
              pickup_latitude: selectedOrder.pickup_latitude,
              pickup_longitude: selectedOrder.pickup_longitude,
              dropoff_latitude: selectedOrder.dropoff_latitude,
              dropoff_longitude: selectedOrder.dropoff_longitude,
            }}
          />
          <div className="w-full col-span-2">
            <div>
              <div>
                <p className="text-lg sm:text-4xl font-bold">
                  Order #{selectedOrder?.id}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Badge
                variant="outline"
                className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3 uppercase"
              >
                {selectedOrder?.order_type}
              </Badge>
              <Badge
                variant="outline"
                className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
              >
                {selectedOrder?.status === "ACCEPTED" ? (
                  <CheckCircle2Icon className="text-green-500 dark:text-green-400" />
                ) : selectedOrder?.status === "REJECTED" ? (
                  <XCircleIcon className="text-red-500 dark:text-red-400" />
                ) : selectedOrder?.status === "COMPLETED" ? (
                  <CheckCircle2Icon className="text-gray-500 dark:text-gray-400" />
                ) : selectedOrder?.status === "NEW_ORDER" ? (
                  <CirclePlus className="text-gray-500 dark:text-gray-400" />
                ) : selectedOrder?.status === "CANCELLED" ? (
                  <BanIcon className="text-red-500 dark:text-red-400" />
                ) : (
                  <LoaderIcon className="text-gray-500 dark:text-gray-400" />
                )}
                {selectedOrder?.status === "NEW_ORDER"
                  ? "New Order"
                  : selectedOrder?.status === "COMPLETED"
                    ? "Completed"
                    : selectedOrder?.status === "ACCEPTED"
                      ? "Approved"
                      : selectedOrder?.status === "REJECTED"
                        ? "Rejected"
                        : selectedOrder?.status === "CANCELLED"
                          ? "Cancelled"
                          : "In Progress"}
              </Badge>
            </div>
            <div className="min-[850px]:flex items-start gap-x-6">
              <div>
                {decodedToken &&
                  decodedToken.permissions.user_role == "owner" && (
                    <div className="mt-5">
                      <h2 className="text-lg font-semibold mb-3">
                        Order Actions
                      </h2>
                      <div className="flex gap-x-3">
                        <AssignStatus id={selectedOrder.id} orderStatus={selectedOrder.status} setStatusChanged={setStatusChanged} />
                        {(selectedOrder.status === "ACCEPTED" ||
                          selectedOrder.status === "ASSIGNED") && (
                          <AssignDriver order={selectedOrder} drivers={drivers} />
                        )}
                      </div>
                    </div>
                  )}
                <h2 className="text-lg font-semibold mb-4 mt-5">Order Info</h2>
                <div className="grid grid-cols-1 min-[450px]:grid-cols-3 gap-4">
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <Calendar className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Pickup date
                    </p>
                    <p className="text-[18px] font-medium">
                      {seperateDateAndTime(selectedOrder?.pickup_time)
                        .datePart || "-"}{" "}
                      {seperateDateAndTime(selectedOrder?.pickup_time)
                        .timePart || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <Euro className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Total price
                    </p>
                    <p className="text-[18px] font-medium">
                      {selectedOrder?.fare
                        ? `${selectedOrder?.fare} ${selectedOrder?.currency}`
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <UserIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Client Name
                    </p>
                    <p className="text-[18px] font-medium">
                      {selectedOrder?.client_name || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <Mail className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Client email
                    </p>
                    <p className="text-[18px] font-medium break-words">
                      {selectedOrder?.email || "-"}
                    </p>
                  </div>
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <PhoneIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-[12px] text-muted-foreground mb-1">
                      Client Phone
                    </p>
                    <p className="text-[18px] font-medium">
                      {selectedOrder?.client_phone || "-"}
                    </p>
                  </div>
                  {selectedOrder?.order_type === "transfer" && (
                    <>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <Ticket className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Flight Number
                        </p>
                        <p className="text-[18px] font-medium break-words overflow-hidden">
                          {selectedOrder?.flight_number
                            ? selectedOrder?.flight_number
                            : "-"}
                        </p>
                      </div>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <StretchVertical className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Distance
                        </p>
                        <p className="text-[18px] font-medium">
                          {selectedOrder?.total_distance_km
                            ? `${selectedOrder?.total_distance_km} km`
                            : "-"}
                        </p>
                      </div>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <Clock className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Duration
                        </p>
                        <p className="text-[18px] font-medium">
                          {selectedOrder?.estimated_duration_minutes ? `${convertMinToH(selectedOrder.estimated_duration_minutes)}` : "-"}
                        </p>
                      </div>
                    </>
                  )}
                  {selectedOrder?.order_type === "tour" && (
                    <>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <UsersIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Total
                        </p>
                        <p className="text-[18px] font-medium">
                          {Number(selectedOrder?.adults || 0) +
                            Number(selectedOrder?.children || 0)}{" "}
                          <span className="text-[12px]">Passenger(s)</span>
                        </p>
                      </div>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <UserIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Adults
                        </p>
                        <p className="text-[18px] font-medium">
                          {selectedOrder?.adults ?? "-"}{" "}
                          <span className="text-[12px]">Adult(s)</span>
                        </p>
                      </div>
                      <div className="bg-muted rounded-xl p-4 text-center">
                        <BabyIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                        <p className="text-[12px] text-muted-foreground mb-1">
                          Children
                        </p>
                        <p className="text-[18px] font-medium">
                          {selectedOrder?.children ?? "-"}{" "}
                          <span className="text-[12px]">Children(s)</span>
                        </p>
                      </div>
                    </>
                  )}
                  {selectedOrder?.order_type === "transfer" && (
                    <div className="bg-muted rounded-xl p-4 text-center">
                      <Car className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                      <p className="text-[12px] text-muted-foreground mb-1">
                        Vehicle class
                      </p>
                      <p className="text-[18px] font-medium">
                        {selectedOrder?.requested_vehicle_class ?? "-"}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-5">
                  <p className="text-lg font-semibold mt-5">Notes</p>
                  <p>{selectedOrder?.notes ? selectedOrder?.notes : "-"}</p>
                </div>
              </div>
              <div>
                <OrderTimeline id={selectedOrder?.id} />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default OrderDetails;
